import { Link } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logger } from 'src/logger';
import { ROUTE_CONFIG } from '../../../utilities/RouteConfig';
import { UserActionsEventTypes } from '../das-finsuite-constants';
import { ExpandableGroup, NavLink } from '../../side-navigation-v2/SideNavV2Components';
import { useAdminNavigationItems } from './useAdminNavigationItems';

import '../../side-navigation-v2/HomePageSideNavigationV2.scss';

/**
 * AdminSideNavigationV2 - Custom side navigation for the Admin Console
 * that follows the same design pattern as HomePageSideNavigationV2.
 *
 * Drop-in replacement: swap <AdminSideNavigation /> with <AdminSideNavigationV2 />
 */
export const AdminSideNavigationV2: React.FC = () => {
  const navItems = useAdminNavigationItems();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [activeHref, setActiveHref] = useState('');

  const adminConfig = ROUTE_CONFIG['/admin'];
  const headerText = adminConfig.navigationText || adminConfig.breadcrumbText;
  const headerHref = adminConfig.path;

  useEffect(() => {
    setActiveHref(pathname);
  }, [pathname]);

  const handleNavigate = (href: string, text: string, external?: boolean) => {
    if (external) return;
    logger.info('DaS FinSuite -> User Action: Admin side nav', {
      event_type: UserActionsEventTypes.HOME_PAGE_SIDE_NAV_CLICK,
      value: text
    });
    if (href.startsWith('http://') || href.startsWith('https://')) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(href);
  };

  return (
    <div className="side-nav-v2-container">
      <div className="side-nav-v2-content">
        {/* Admin Console Header */}
        <div className="side-nav-v2-section-header">
          <Link
            href={headerHref}
            onFollow={(e) => {
              e.preventDefault();
              handleNavigate(headerHref, headerText);
            }}
          >
            {headerText}
          </Link>
        </div>
        <div className="side-nav-v2-divider" />

        {/* Navigation Items */}
        {navItems.map((item, index) => {
          if (item.type === 'divider') {
            return <div key={`divider-${index}`} className="side-nav-v2-divider" />;
          }
          if (item.type === 'section-header') {
            return (
              <div key={`section-header-${index}`} className="side-nav-v2-section-header">
                {item.href ? (
                  <Link
                    href={item.href}
                    onFollow={(e) => {
                      e.preventDefault();
                      handleNavigate(item.href, item.text);
                    }}
                  >
                    {item.text}
                  </Link>
                ) : (
                  item.text
                )}
              </div>
            );
          }
          if (item.type === 'expandable-group') {
            return <ExpandableGroup key={item.href} item={item} activeHref={activeHref} onNavigate={handleNavigate} />;
          }
          return <NavLink key={item.href} item={item} activeHref={activeHref} onNavigate={handleNavigate} />;
        })}

        <div className="side-nav-v2-divider" />
      </div>
    </div>
  );
};
